import { Amount } from 'uom';
import { makeAmountObject, makeAmountWith } from './amount.helpers';
import { AmountObject, ReduceOptionInterface } from './amount.model';
import { ZERO_SF } from './units-definition';

/**
 * Adds a to b
 * @param a Amount
 * @param b Amount
 */
export const addition = (a, b): Amount.Amount<any> => Amount.plus(a, b);

/**
 * Substract b from a
 * @param a Amount
 * @param b Amount
 */
export const subtract = (a, b): Amount.Amount<any> => Amount.minus(a, b);

const reduceWith = (items: AmountObject[], { operation, initialValue }: ReduceOptionInterface) =>
  items.map(amountData => makeAmountWith(amountData)).reduce((acc, curr) => operation(acc, curr), initialValue);

/**
 * Reduces a list of `AmountObject` using specific `operation` (`addition`, `substract`)
 * @param items `AmountObject[]`
 * @param options `ReduceOptionInterface`
 */
export const reduceAmountWith = (
  items: any[],
  { operation, initialValue, dataset, defaultUom }: ReduceOptionInterface
) =>
  dataset
    ? reduceWith(
        items.map(item => makeAmountObject(item, dataset, defaultUom)),
        { operation, initialValue }
      )
    : reduceWith(items, { operation, initialValue });

/**
 * Calculates the average value from list of values with `{value: number, uom: string }` and returns
 * the average. if any value is invalid 0 is passed. optional param is Unit which will return the value
 * in its equivalence
 * @param {{ value: number, uom: string }[]} [values]
 * @returns `Amount`
 */
export const average = (values: Array<{ value: number; uom: string }>) => {
  const length = values.length;
  const sum = values
    .map(({ value, uom }) => makeAmountWith({ value: value || 0, uom }))
    .reduce((acc, curr) => addition(acc, curr), ZERO_SF);
  return Amount.divide(sum, length || 1);
};

/**
 * Calculates the average of a list of object using a dataset object to read values & respective uoms to
 * create Amounts and calculate the average
 * @param {any[]} items
 * @param {{value: string; uom: string; defaultUom?: string;}} options
 *   `value` path to read value from object
 *    `uom` path to read uom from object
 *    `defaultUom` path to read uom from object
 */
export const averageOfDataset = (
  items: any[],
  { defaultUom, skipZeros = false, ...dataset }: { value: string; uom: string; defaultUom: string; skipZeros: boolean }
) => {
  const values = items
    .map(item => makeAmountObject(item, dataset, defaultUom))
    .filter(({ value }) => (skipZeros ? !!value : true));
  return average(values);
};

export const calcPercentage = (dividend, divisor) => (dividend / divisor) * 100;
